import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import HaveYouBeenToThisClinicBefore from '../../../components/HaveYouBeenToThisClinicBefore';
// @ts-expect-error ts-migrate(2307) FIXME: Cannot find module '../../MobileBookingWidgetSecon... Remove this comment to see the full error message
import s from '../../MobileBookingWidgetSecondForm.css';
import TextInput from '../../../../Form/TextInput';
import {
  BIRTH_DATE_FIELD_NAME,
  BIRTH_SEX_FIELD_NAME,
  EMAIL_FIELD_NAME,
  FIRST_NAME_FIELD_NAME,
  getMobileSecondFormRequiredFields,
  LAST_NAME_FIELD_NAME,
  PHONE_FIELD_NAME,
} from '../../../util/validators';
import {
  BOOKING_WIDGET_TOUCH_DOB,
  BOOKING_WIDGET_TOUCH_EMAIL,
  BOOKING_WIDGET_TOUCH_FIRST_NAME,
  BOOKING_WIDGET_TOUCH_LAST_NAME,
  BOOKING_WIDGET_TOUCH_PATIENT_SEX,
  BOOKING_WIDGET_TOUCH_PHONE,
} from '../../../../../core/analytics/events';

import BirthDateInput from '../../../../Form/BirthDateInput';
import {
  birthDateInputValidator,
  emailInputValidator,
  maskedMobilePhoneInputValidator,
} from '../../../../../core/util/rcForm/validators';
import { TELEMED_LOCATIONS_WITH_MINIMUM_AGE_REQUIREMENT } from '../../../../../config/locations';
import {
  shouldRequireEmailInBookingWidget,
  isBirthSexRequired,
  isLegalNameRequired,
} from '../../../../../core/util/location';
import PatientSexSelectInput from '../../../../Form/PatientSexSelectInput';
import MobilePhoneInput from '../../../../Form/MaskedMobilePhoneInput';
import { isEmptyArray } from '../../../../../core/util/array';
import { getDisplayableUserProfilesInAccountSummary } from '../../../../../core/util/account';
import { biggerThan } from '../../../../../core/util/styledComponents';
import { colors } from '../../../../../constants/colors';
import { getNewBookingLocale } from '../../../../../selectors/newBooking';
import { useBookingWidgetIsFamilyBooking } from '~/hooks/redux/useBookingWidgetFamilyBookingData';

const UserInfoTitle = styled.div.withConfig({ componentId: 'sc-eoyc3i-0' })`
  display: flex;
  justify-content: space-between;
  margin: 0 10px 10px 20px;
`;

const SectionTitle = styled.div.withConfig({ componentId: 'sc-eoyc3i-1' })`
  display: flex;
  justify-content: space-between;

  ${biggerThan.md`
    margin: 0 4px;
  `};
`;

const FormUserInfoBlock = styled.div.withConfig({ componentId: 'sc-eoyc3i-2' })`
  margin-bottom: 20px;
  background-color: ${colors.white};
`;

type Props = {
  isLoggedIn: boolean;
  accountSummary?: any;
  isExistingPatient?: any;
  handleAndTrackLoginFromReturningPatientToggle: (...args: any[]) => any;
  form: any;
  location: any;
  initialValues: any;
  submitted?: boolean;
  firstName: any;
  lastName: any;
  setZIndexGivenError: (...args: any[]) => any;
  birthDate: any;
  birthSex: any;
  phone: any;
  email: any;
  onMobilePhoneInputBlur: (...args: any[]) => any;
  getAddressFields: (...args: any[]) => any;
  phoneFieldTouched: boolean;
  newProfilePending: boolean;
};

const UserProfileFields = (props: Props) => {
  const {
    isLoggedIn,
    accountSummary,
    isExistingPatient,
    handleAndTrackLoginFromReturningPatientToggle,
    form,
    location,
    initialValues,
    submitted,
    firstName,
    lastName,
    setZIndexGivenError,
    birthDate,
    birthSex,
    phone,
    email,
    onMobilePhoneInputBlur,
    getAddressFields,
    phoneFieldTouched,
    newProfilePending,
  } = props;

  const { t } = useTranslation(['bookingWidget', 'common']);

  const locale = useSelector(getNewBookingLocale);
  const isFamilyVisitActivatedInBookingWidget = useBookingWidgetIsFamilyBooking();

  // if user logged in and has an available user profile to choose from don't show this section
  if (
    isLoggedIn &&
    !isEmptyArray(getDisplayableUserProfilesInAccountSummary(accountSummary)) &&
    !newProfilePending
  ) {
    return null;
  }

  const shouldUseLegalNameTreatment = isLegalNameRequired(location);

  const firstNamePlaceholderText = shouldUseLegalNameTreatment
    ? t('bookingWidget:userProfileAndPatientInfo.legalFirstName')
    : t('bookingWidget:userProfileAndPatientInfo.patientFirstName');
  const lastNamePlaceholderText = shouldUseLegalNameTreatment
    ? t('bookingWidget:userProfileAndPatientInfo.legalLastName')
    : t('bookingWidget:userProfileAndPatientInfo.patientLastName');

  return (
    <div>
      {!isLoggedIn && !newProfilePending && (
        <HaveYouBeenToThisClinicBefore
          accountSummary={accountSummary}
          form={form}
          handleAndTrackLoginFromReturningPatientToggle={
            handleAndTrackLoginFromReturningPatientToggle
          }
          initialValues={initialValues}
          isExistingPatient={isExistingPatient}
          isLoggedIn={isLoggedIn}
          location={location}
        />
      )}

      <UserInfoTitle>
        <SectionTitle>
          {t('bookingWidget:userProfileAndPatientInfo.title')}
          {!!isFamilyVisitActivatedInBookingWidget && ' #1'}
        </SectionTitle>
      </UserInfoTitle>

      <FormUserInfoBlock className={s.formUserInfoBlock}>
        <div className={s.row} style={setZIndexGivenError(firstName)}>
          <div className={s.firstName}>
            <TextInput
              errorInline
              field={firstName}
              funPlaceholder
              id={FIRST_NAME_FIELD_NAME}
              inputProps={form.getFieldProps(FIRST_NAME_FIELD_NAME, {
                rules: [
                  {
                    required: getMobileSecondFormRequiredFields(location, form).includes(
                      FIRST_NAME_FIELD_NAME
                    ),
                    message: t('common:required'),
                  },
                ],
                initialValue: initialValues[FIRST_NAME_FIELD_NAME],
              })}
              placeholder={firstNamePlaceholderText}
              placeholderContainerName={s.inputPlaceholder}
              s={s}
              showErrors={false}
              submitted={submitted}
              trackEvent={BOOKING_WIDGET_TOUCH_FIRST_NAME}
            />
          </div>
        </div>

        <div className={s.row} style={setZIndexGivenError(lastName)}>
          <div className={s.lastName}>
            <TextInput
              errorInline
              field={lastName}
              funPlaceholder
              id={LAST_NAME_FIELD_NAME}
              inputProps={form.getFieldProps(LAST_NAME_FIELD_NAME, {
                rules: [
                  {
                    required: getMobileSecondFormRequiredFields(location, form).includes(
                      LAST_NAME_FIELD_NAME
                    ),
                    message: t('common:required'),
                  },
                ],
                initialValue: initialValues[LAST_NAME_FIELD_NAME],
              })}
              placeholder={lastNamePlaceholderText}
              placeholderContainerName={s.inputPlaceholder}
              s={s}
              showErrors={false}
              submitted={submitted}
              trackEvent={BOOKING_WIDGET_TOUCH_LAST_NAME}
            />
          </div>
        </div>

        <div className={s.row} style={setZIndexGivenError(birthDate)}>
          <BirthDateInput
            dayPlaceholder={t('common:day')}
            errorInline
            field={birthDate}
            funPlaceholder
            id={BIRTH_DATE_FIELD_NAME}
            inputProps={form.getFieldProps(BIRTH_DATE_FIELD_NAME, {
              rules: [
                {
                  required: getMobileSecondFormRequiredFields(location, form).includes(
                    BIRTH_DATE_FIELD_NAME
                  ),
                  message: t('common:required'),
                  validator: birthDateInputValidator,
                  hasTelemedMinimumAgeRequirement:
                    TELEMED_LOCATIONS_WITH_MINIMUM_AGE_REQUIREMENT.includes(location.id),
                },
              ],
              initialValue: initialValues[BIRTH_DATE_FIELD_NAME],
            })}
            locale={locale}
            monthPlaceholder={t('bookingWidget:userProfileAndPatientInfo.birthMonth')}
            placeholderContainerName={s.inputPlaceholder}
            s={s}
            showErrors={false}
            submitted={submitted}
            trackEvent={BOOKING_WIDGET_TOUCH_DOB}
            validateField={form.validateFields}
            yearPlaceholder={t('common:year')}
          />
        </div>

        {isBirthSexRequired(location) && (
          <div
            className={s.row}
            data-testid="patient-sex-select-input"
            style={setZIndexGivenError(birthSex)}
          >
            <div className={s.email}>
              <PatientSexSelectInput
                errorInLine
                field={birthSex}
                funPlaceholder
                id={BIRTH_SEX_FIELD_NAME}
                inputProps={form.getFieldProps(BIRTH_SEX_FIELD_NAME, {
                  rules: [
                    {
                      required: getMobileSecondFormRequiredFields(location, form).includes(
                        BIRTH_SEX_FIELD_NAME
                      ),
                      message: t('common:required'),
                    },
                  ],
                  initialValue: initialValues[BIRTH_SEX_FIELD_NAME],
                })}
                placeholder={t('common:userInfo.patientSex.capitalize')}
                s={s}
                showErrors={false}
                submitted={submitted}
                trackEvent={BOOKING_WIDGET_TOUCH_PATIENT_SEX}
                validateField={form.validateFields}
              />
            </div>
          </div>
        )}
        {!newProfilePending && (
          <>
            <div className={s.row} style={setZIndexGivenError(phone)}>
              <div className={s.phone}>
                <div
                  className={
                    (submitted || phoneFieldTouched) && phone.error
                      ? s.formFieldInlineError
                      : s.formField
                  }
                >
                  <div className={s.formInputContainer}>
                    <MobilePhoneInput
                      enableMask
                      // @ts-expect-error ts-migrate(2322) FIXME: Property 'errorInline' does not exist... Remove this comment to see the full error message
                      errorInline
                      field={phone}
                      funPlaceholder
                      id={PHONE_FIELD_NAME}
                      inputProps={form.getFieldProps(PHONE_FIELD_NAME, {
                        rules: [
                          {
                            required: getMobileSecondFormRequiredFields(location, form).includes(
                              PHONE_FIELD_NAME
                            ),
                            validator: maskedMobilePhoneInputValidator,
                          },
                        ],
                        initialValue: initialValues[PHONE_FIELD_NAME],
                      })}
                      onBlur={onMobilePhoneInputBlur}
                      onChange={phone.onChange}
                      placeholder={t('bookingWidget:userProfileAndPatientInfo.mobileNumber')}
                      placeholderContainerName={s.inputPlaceholder}
                      s={s}
                      showErrors={false}
                      trackEvent={BOOKING_WIDGET_TOUCH_PHONE}
                      type="phone"
                      validateField={form.validateFields}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className={s.row} style={setZIndexGivenError(email)}>
              <div className={s.email} data-testid="email-field">
                <TextInput
                  errorInline
                  field={email}
                  funPlaceholder
                  id={EMAIL_FIELD_NAME}
                  inputProps={form.getFieldProps(EMAIL_FIELD_NAME, {
                    rules: [
                      {
                        required: getMobileSecondFormRequiredFields(location, form).includes(
                          EMAIL_FIELD_NAME
                        ),
                        validator: emailInputValidator,
                      },
                    ],
                    initialValue: initialValues[EMAIL_FIELD_NAME],
                  })}
                  placeholder={
                    shouldRequireEmailInBookingWidget(location)
                      ? t('bookingWidget:userProfileAndPatientInfo.emailAddress')
                      : t('bookingWidget:userProfileAndPatientInfo.emailAddressOptional')
                  }
                  placeholderContainerName={s.inputPlaceholder}
                  s={s}
                  showErrors={false}
                  submitted={submitted}
                  trackEvent={BOOKING_WIDGET_TOUCH_EMAIL}
                  type="email"
                />
              </div>
            </div>
          </>
        )}
      </FormUserInfoBlock>
    </div>
  );
};

export default UserProfileFields;
